<template>
  <v-dialog persistent max-width="800px" v-model="dialog" fullscreen transition="dialog-bottom-transition">
    <div class="course-edit-wrapper">
      <v-app-bar app color="white" flat class="app-bar-border">
        <v-btn icon @click="closeDialog()">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-toolbar-title v-if="courseEdit" class="font-weight-bold"> Kurs bearbeiten </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn text color="primary" @click="showPushPopupMethod()">
          <v-icon left>mdi-check</v-icon>
          Speichern
        </v-btn>
      </v-app-bar>

      <v-container v-if="!courseEdit" class="d-flex justify-center align-center" style="height: 100vh">
        <v-progress-circular indeterminate color="primary" size="48"></v-progress-circular>
      </v-container>

      <v-container v-else class="form-container">
        <div class="header-image-container">
          <v-img :src="headerImageSrc" class="header-image" height="250px" gradient="to top right, rgba(0,0,0,.3), rgba(25,32,72,.7)">
            <v-row class="fill-height ma-0" align="end" justify="start">
              <v-card-title class="white--text text-h5 font-weight-bold">
                {{ courseEdit.course.name }}
              </v-card-title>
            </v-row>
          </v-img>
        </div>

        <v-row>
          <v-col cols="12">
            <v-card class="form-card" outlined>
              <v-card-title>Grundlagen</v-card-title>
              <v-card-text>
                <course-search :value="courseEdit.course" label="Kursformat" rights="gf_c_plan" @update="updateCourse" outlined></course-search>
                <v-select
                  v-model="courseEdit.label"
                  :items="labels"
                  item-text="text"
                  item-value="_id"
                  label="Label (optional)"
                  outlined
                  class="mt-4"
                  clearable></v-select>
                <v-select
                  v-if="events.length"
                  v-model="courseEdit.event"
                  :items="events"
                  item-text="title"
                  item-value="_id"
                  label="Event (optional)"
                  outlined
                  class="mt-4"
                  clearable></v-select>
                <v-text-field
                  v-model="courseEdit.additionalInfo"
                  outlined
                  label="Zusatzinfo (optional)"
                  placeholder="z.B. Mindest-Teilnehmerzahl"></v-text-field>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12">
            <v-card class="form-card" outlined>
              <v-card-title>Zeit, Ort & Teilnehmer</v-card-title>
              <v-card-text>
                <v-row>
                  <v-col cols="6"><v-text-field v-model="time" type="time" outlined label="Uhrzeit"></v-text-field></v-col>
                  <v-col cols="6"><v-text-field v-model="courseEdit.duration" type="number" outlined label="Dauer (Min)"></v-text-field></v-col>
                </v-row>
                <v-select v-model="courseEdit.room" :items="rooms" item-text="name" item-value="_id" outlined label="Raum"></v-select>
                <v-switch v-model="courseEdit.reqSignup" label="Anmeldung erforderlich" class="mt-4"></v-switch>
                <v-text-field v-if="courseEdit.reqSignup" v-model="courseEdit.amount" type="number" outlined label="Anzahl Plätze"></v-text-field>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12">
            <v-card class="form-card" outlined>
              <v-card-title>Kursleiter & Darstellung</v-card-title>
              <v-card-text>
                <div v-for="(instructor, index) in courseEdit.instructors" :key="index" class="d-flex align-center">
                  <user-search
                    :key="index + instructor + '-inst'"
                    instructor
                    :value="instructor"
                    label="Instruktor"
                    rights="gf_c_plan"
                    @update="(newVal) => updateInstructor(newVal, instructor)"
                    outlined
                    class="flex-grow-1"></user-search>
                  <v-btn icon color="grey" @click="removeInstructor(instructor)" class="ml-2"><v-icon>mdi-delete</v-icon></v-btn>
                </div>
                <v-btn text color="primary" @click="addInstructor" class="mt-2"><v-icon left>mdi-plus</v-icon> Instruktor hinzufügen</v-btn>
                <v-divider class="my-6"></v-divider>
                <v-subheader>Kurs-Icon</v-subheader>
                <v-chip-group v-model="courseEdit.icon" column active-class="primary">
                  <v-chip large v-for="ic in icons" :key="ic" :value="ic" filter>
                    <v-avatar class="iconAvatar" center>
                      <lottie-player mode="normal" style="width: 60px; height: 60px" :src="'/api/gf/icons/' + ic"></lottie-player>
                    </v-avatar>
                  </v-chip>
                </v-chip-group>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12">
            <v-card class="form-card" outlined style="border-color: #d32f2f">
              <v-card-title class="red--text text--darken-2">Kurs-Status</v-card-title>
              <v-card-text>
                <p class="body-1">Sage diesen einzelnen Kurs ab oder reaktiviere ihn wieder. Die Kursplanung bleibt davon unberührt.</p>
                <v-btn
                  block
                  large
                  :dark="!courseEdit.cancelled"
                  :outlined="courseEdit.cancelled"
                  :color="courseEdit.cancelled ? 'grey' : 'red darken-2'"
                  @click="changeCourseActivation">
                  <v-icon left>{{ courseEdit.cancelled ? "mdi-check-circle-outline" : "mdi-cancel" }}</v-icon>
                  {{ courseActivationText }}
                </v-btn>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>

      <v-dialog v-model="showCancelledPopup" persistent max-width="480" aria-labelledby="cancel-dialog-title">
        <v-card class="course-dialog">
          <div class="course-dialog-header">
            <div class="course-dialog-icon"><v-icon color="primary">mdi-eye-outline</v-icon></div>
            <div>
              <div class="course-dialog-eyebrow">Kursabsage</div>
              <h2 id="cancel-dialog-title">Kurs weiterhin anzeigen?</h2>
            </div>
          </div>
          <v-card-text class="course-dialog-body">
            <p>Soll der abgesagte Kurs für deine Kunden in der Kursliste sichtbar bleiben?</p>
            <div class="course-dialog-note">
              <v-icon color="primary" small>mdi-information-outline</v-icon>
              <span>Wenn du ihn anzeigst, wird er als «abgesagt» markiert.</span>
            </div>
          </v-card-text>
          <v-card-actions class="course-dialog-actions">
            <v-btn outlined class="course-dialog-secondary" @click="cancelledCourseVisible(false)">Kurs ausblenden</v-btn>
            <v-btn depressed color="primary" @click="cancelledCourseVisible(true)">Sichtbar lassen</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="showPushPopUp" persistent max-width="520" aria-labelledby="push-dialog-title">
        <v-card class="course-dialog">
          <div class="course-dialog-header">
            <div class="course-dialog-icon"><v-icon color="primary">mdi-bell-outline</v-icon></div>
            <div>
              <div class="course-dialog-eyebrow">Änderungen speichern</div>
              <h2 id="push-dialog-title">Teilnehmer informieren?</h2>
            </div>
          </div>
          <v-card-text class="course-dialog-body">
            <p>Möchtest du die bereits angemeldeten Teilnehmer per Push-Nachricht über die Änderungen informieren?</p>
          </v-card-text>
          <v-card-actions class="course-dialog-actions">
            <v-btn outlined class="course-dialog-secondary" @click="saveUpdatedCourse(false)">Nur speichern</v-btn>
            <v-btn depressed color="primary" @click="saveUpdatedCourse(true)">Speichern & informieren</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="showSMSDialog" persistent max-width="600" aria-labelledby="sms-dialog-title">
        <v-card class="course-dialog">
          <div class="course-dialog-header">
            <div class="course-dialog-icon" :class="smsSendingState === 'result' ? 'course-dialog-icon--' + smsResultTone : ''">
              <v-icon :color="smsSendingState === 'result' ? smsResultTone : 'primary'">
                {{ smsSendingState === "result" ? smsResultIcon : "mdi-message-text-outline" }}
              </v-icon>
            </div>
            <div>
              <div class="course-dialog-eyebrow">Kursabsage · SMS</div>
              <h2 id="sms-dialog-title">{{ smsSendingState === "result" ? smsResultTitle : smsSendingState === "sending" ? "SMS werden gesendet" : "Teilnehmer per SMS informieren" }}</h2>
            </div>
          </div>
          <v-card-text class="course-dialog-body">
            <div v-if="smsSendingState === 'preview'">
              <p>Informiere die Teilnehmer mit einer persönlichen Nachricht über die Kursabsage.</p>
              <div class="sms-message">
                <div class="course-dialog-eyebrow">Nachrichtenvorschau</div>
                <p>{{ exampleMessage }}</p>
              </div>
              <div class="sms-section-heading">
                <h3>Empfänger <span class="sms-count">{{ smsRecipients.length }}</span></h3>
                <span>{{ validRecipientsCount }} mit gültiger Nummer</span>
              </div>
              <ul class="sms-recipients">
                <li v-for="(recipient, index) in smsRecipients" :key="index" class="sms-recipient">
                  <div class="sms-recipient-details">
                    <div class="sms-recipient-name">{{ recipient.name }}</div>
                    <div class="sms-recipient-phone">
                      <span>{{ recipient.phoneNumber || "Keine Nummer" }}</span>
                      <v-tooltip bottom v-if="recipient.wasModified">
                        <template v-slot:activator="{ on, attrs }">
                          <button type="button" class="sms-phone-info" aria-label="Ursprüngliche Telefonnummer anzeigen" v-bind="attrs" v-on="on">
                            <v-icon small color="grey darken-1">mdi-information-outline</v-icon>
                          </button>
                        </template>
                        <span class="sms-phone-tooltip">Original: {{ recipient.originalPhone }}</span>
                      </v-tooltip>
                    </div>
                  </div>
                  <span class="sms-recipient-status" :class="{ 'sms-recipient-status--invalid': !recipient.isValid }">
                    <v-icon small :color="recipient.isValid ? 'primary' : 'error'">{{ recipient.isValid ? "mdi-check-circle-outline" : "mdi-alert-circle-outline" }}</v-icon>
                    {{ recipient.isValid ? "Bereit" : "Ungültig" }}
                  </span>
                </li>
              </ul>
              <div v-if="invalidRecipientsCount > 0" class="course-dialog-note course-dialog-note--warning">
                <v-icon color="warning" small>mdi-alert-circle-outline</v-icon>
                <span>{{ invalidRecipientsCount }} {{ invalidRecipientsCount === 1 ? "Person hat" : "Personen haben" }} keine gültige Mobilnummer und {{ invalidRecipientsCount === 1 ? "erhält" : "erhalten" }} keine SMS.</span>
              </div>
            </div>

            <div v-if="smsSendingState === 'sending'" class="sms-sending" role="status" aria-live="polite">
              <v-progress-circular indeterminate color="primary" size="48" width="3"></v-progress-circular>
              <h3>Versand läuft …</h3>
              <p>Die Nachrichten werden für {{ validRecipientsCount }} {{ validRecipientsCount === 1 ? "Person" : "Personen" }} versendet. Bitte warte einen Moment.</p>
            </div>

            <div v-if="smsSendingState === 'result'" role="status" aria-live="polite">
              <p>{{ smsResultSummary }}</p>
              <div v-if="!smsHasError" class="sms-result-stats">
                <div><strong>{{ smsSentCount }}</strong><span>Versendet</span></div>
                <div :class="{ 'sms-stat-failed': smsFailed.length > 0 }"><strong>{{ smsFailed.length }}</strong><span>Fehlgeschlagen</span></div>
                <div v-if="invalidRecipientsCount > 0"><strong>{{ invalidRecipientsCount }}</strong><span>Ohne gültige Nummer</span></div>
              </div>
              <div v-if="smsFailed.length > 0" class="sms-failures">
                <h3>Diese Personen wurden nicht erreicht</h3>
                <ul>
                  <li v-for="(fail, index) in smsFailed" :key="fail.userId || index">
                    <strong>{{ fail.name }}</strong><span>{{ fail.error }}</span>
                  </li>
                </ul>
              </div>
              <div v-if="!smsHasError && smsSentCount > 0" class="course-dialog-note">
                <v-icon color="primary" small>mdi-information-outline</v-icon>
                <span>«Versendet» bedeutet, dass der SMS-Dienst die Nachricht angenommen hat. Die Zustellung ist damit noch nicht bestätigt.</span>
              </div>
            </div>
          </v-card-text>
          <v-card-actions v-if="smsSendingState !== 'sending'" class="course-dialog-actions">
            <template v-if="smsSendingState === 'preview'">
              <v-btn outlined class="course-dialog-secondary" @click="showSMSDialog = false">Abbrechen</v-btn>
              <v-btn depressed color="primary" @click="sendSMS" :disabled="validRecipientsCount === 0">
                <v-icon left small>mdi-send</v-icon> {{ validRecipientsCount }} SMS senden
              </v-btn>
            </template>
            <v-btn v-if="smsSendingState === 'result'" depressed color="primary" @click="showSMSDialog = false">Schliessen</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </v-dialog>
</template>

<script>
import api from "../../api"
import { mapActions } from "vuex"
import userSearch from "../../components/userSearch.vue"
import courseSearch from "../../components/courseSearch.vue"
export default {
  name: "courseListEdit",
  components: { userSearch, courseSearch },
  data() {
    return {
      loading: true,
      dialog: true,
      showCancelledPopup: false,
      showPushPopUp: false,
      courseEdit: null,
      time: "00:00:00",
      rooms: [],
      labels: [{ _id: null, text: "Kein Label" }],
      icons: [],
      events: [], // Beibehalten falls benötigt, im Template aber entfernt
      // SMS Logic
      showSMSDialog: false,
      smsRecipients: [],
      smsMessage: "",
      smsSendingState: "preview", // 'preview', 'sending', 'result'
      smsResultSummary: "",
      smsFailed: [],
      smsSentCount: 0,
      smsHasError: false,
    }
  },
  async created() {
    this.setNavigationBarText(null) // Wir verwenden jetzt die v-app-bar

    try {
      const [dbInfos, iconsData, eventsData] = await Promise.all([
        api.fetchCourseEdit(this.$route.params.id),
        api.fetchAllGFIcons(),
        api.fetchEventsForSelect(),
      ])

      this.rooms = dbInfos.rooms
      this.labels = this.labels.concat(dbInfos.labels)
      this.courseEdit = dbInfos.execution
      this.icons = iconsData.icons
      this.events = eventsData // Events-Daten sind verfügbar

      if (this.events.length) this.events.unshift({ _id: null, title: "Kein Event" })

      // Initialwerte setzen
      this.courseEdit.cancelledVisibility = this.courseEdit.cancelledVisibility || false
      this.courseEdit.room = this.courseEdit.room || null
      this.courseEdit.event = this.courseEdit.event || null
      this.courseEdit.additionalInfo = this.courseEdit.additionalInfo || ""
      this.courseEdit.icon = this.courseEdit.icon || "dumbell.json"

      const hours = String(this.courseEdit.hours).padStart(2, "0")
      const minutes = String(this.courseEdit.minutes).padStart(2, "0")
      this.time = `${hours}:${minutes}`

      this.loading = false
    } catch (error) {
      this.$toast.error("Kurs konnte nicht geladen werden.")
      this.closeDialog()
    }
  },
  methods: {
    ...mapActions("navigationStoreNotPersisted", ["setNavigationBarText"]),
    showPushPopupMethod() {
      if (this.courseEdit.instructors.includes(null)) {
        this.$toast.error("Bitte wähle für alle Felder einen Instruktor aus oder entferne die leeren Felder.")
        return // <-- Hier wird abgebrochen
      }
      this.showPushPopUp = true
    },
    cancelledCourseVisible(visible) {
      this.courseEdit.cancelledVisibility = visible
      this.showCancelledPopup = false
      // Trigger SMS dialog preparation after cancellation decision
      this.prepareSMSDialog()
    },

    async prepareSMSDialog() {
      this.smsSendingState = "preview"
      this.smsRecipients = []
      this.smsFailed = []
      this.smsResultSummary = ""
      this.smsSentCount = 0
      this.smsHasError = false

      // Extract day and month to save characters (e.g., "19.02.")
      const shortDate = new Date(this.courseEdit.ts * 1000).toLocaleDateString("de-CH", { day: "2-digit", month: "2-digit" })
      const time = this.$moment(this.courseEdit.ts * 1000)
        .hours(this.courseEdit.hours)
        .minutes(this.courseEdit.minutes)
        .format("HH:mm")

      const cleanForGSM7 = (text) => {
        return (
          text
            .replace(/®/g, "") // Registered Mark entfernen
            .replace(/©/g, "") // Copyright entfernen
            .replace(/™/g, "") // Trademark entfernen
            // Fallback: Alles, was nicht GSM-7-kompatibel ist, entfernen oder normalisieren
            .normalize("NFD")
            .replace(/[^\u0000-\u007FäöüÄÖÜss€\n\r]/g, "")
        )
      }
      const rawCourseTitle = this.courseEdit.course.name
      const courseTitle = cleanForGSM7(rawCourseTitle)

      try {
        const viewData = await api.fetchCourseView(this.courseEdit._id, 0)
        if (viewData?.courseView?.signups) {
          this.smsRecipients = viewData.courseView.signups.map((s) => {
            const user = s.user
            const rawPhone = user.telefon
            const cleanPhone = this.normalizePhoneNumber(rawPhone)
            const isValid = this.validatePhoneNumber(cleanPhone)

            let greeting = "Hallo"
            if (user.geschlecht === "m") greeting = "Lieber"
            if (user.geschlecht === "w") greeting = "Liebe"

            const fullName = `${user.vorname} ${user.nachname}`

            // Optimized message: ~135 characters base length.
            // Leaves ~25 characters buffer for long names/courses before hitting the 160 limit.
            const msgText = `${greeting} ${user.vorname}, der Kurs ${courseTitle} am ${shortDate} um ${time} Uhr fällt mangels Anmeldungen leider aus. Danke für dein Verständnis. WELL COME FIT Team`

            return {
              userId: user._id,
              name: fullName,
              phoneNumber: cleanPhone,
              originalPhone: rawPhone,
              wasModified: rawPhone !== cleanPhone && isValid,
              isValid: isValid,
              message: msgText,
            }
          })

          if (this.smsRecipients.length > 0) {
            this.showSMSDialog = true
          } else {
            this.$toast.info("Keine Teilnehmer angemeldet.")
          }
        }
      } catch (err) {
        console.error("Could not fetch participants for SMS", err)
        this.$toast.error("Fehler beim Laden der Teilnehmer für SMS.")
      }
    },

    async sendSMS() {
      this.smsSendingState = "sending"
      this.smsHasError = false
      this.smsSentCount = 0
      this.smsFailed = []
      // Wir filtern nur valide Empfänger
      const validRecipients = this.smsRecipients.filter((r) => r.isValid)

      try {
        // Wir senden jetzt das Array mit den individuellen Messages
        const response = await api.sendCancellationSMS({
          courseId: this.courseEdit._id,
          recipients: validRecipients, // Hier sind jetzt die { message: "..." } Objekte drin
        })

        this.smsSendingState = "result"
        this.smsResultSummary = response.message

        if (response.results) {
          this.smsSentCount = response.results.filter((r) => r.status === "success").length
          const failures = response.results.filter((r) => r.status === "failed")
          this.smsFailed = failures.map((f) => {
            const rec = this.smsRecipients.find((r) => r.userId === f.userId)
            return {
              userId: f.userId,
              name: rec ? rec.name : "Unbekannte Person",
              error: f.error,
            }
          })
        }
      } catch (e) {
        this.smsSendingState = "result"
        this.smsHasError = true
        this.smsResultSummary = "Der Versand konnte nicht bestätigt werden. Bitte prüfe den Versandstatus, bevor du die Nachrichten erneut sendest."
        console.error(e)
      }
    },
    validatePhoneNumber(phone) {
      if (!phone) return false
      // Simple validation: check if it has at least 10 digits/chars approx
      // E.164 format usually starts with +
      const cleaned = phone.replace(/[^\d+]/g, "")
      return cleaned.length >= 10 && cleaned.includes("+")
    },

    normalizePhoneNumber(phone) {
      if (!phone) return ""
      let cleaned = phone.trim()

      // Remove spaces
      cleaned = cleaned.replace(/\s/g, "")

      // Check for 00 start -> replace with +
      if (cleaned.startsWith("00")) {
        return "+" + cleaned.substring(2)
      }

      // Check for 0 start (and not 00) -> replace with +41
      if (cleaned.startsWith("0") && !cleaned.startsWith("00")) {
        return "+41" + cleaned.substring(1)
      }

      // If it starts with +, assume it's okay or at least intentional
      if (cleaned.startsWith("+")) {
        return cleaned
      }

      // Fallback: If no +, add +41 (unsafe assumption? usually correct for local context)
      // But user specifically asked for 076... -> +4176...
      return "+41" + cleaned
    },

    async saveUpdatedCourse(sendPush) {
      console.log("Instruktoren", this.courseEdit.instructors)
      this.showPushPopUp = false
      if (this.courseEdit.instructors.includes(null)) {
        this.$toast.error("Bitte wähle für alle Felder einen Instruktor aus oder entferne die leeren Felder.")
        return // <-- Hier wird abgebrochen
      }
      let updatedCourse = JSON.parse(JSON.stringify(this.courseEdit))
      updatedCourse.course = updatedCourse.course._id
      updatedCourse.sendPush = sendPush

      try {
        await api.updateCourseExecution(updatedCourse)
        this.$toast.success("Kurs erfolgreich aktualisiert!")
        this.$emit("updateCourse")
        this.closeDialog()
      } catch (e) {
        this.$toast.error("Speichern fehlgeschlagen.")
      }
    },

    changeCourseActivation() {
      this.courseEdit.cancelled = !this.courseEdit.cancelled
      if (this.courseEdit.cancelled) {
        this.showCancelledPopup = true
        // Prepare SMS Dialog but don't show it yet (wait for visibility decision or show immediately if preferred?)
        // The user requirement says: "wenn der Kurs abgesagt wird, eine checkbox / nachfrage an den benutzer machen, ob er direkt alle teilnehmer des kurses per SMS benachrichtigen möchte"
        // We'll hook into the cancelledCourseVisible method or add a secondary step.
      } else {
        this.courseEdit.cancelledVisibility = false
      }
    },

    removeInstructor(id) {
      console.log("Removing instructor with ID:", id)
      this.courseEdit.instructors = this.courseEdit.instructors.filter((instructorId) => instructorId !== id)
    },

    addInstructor() {
      if (!this.courseEdit.instructors.includes(null)) {
        this.courseEdit.instructors.push(null)
      }
    },

    updateCourse(newCourse) {
      this.courseEdit.course = newCourse
    },

    updateInstructor(newId, oldId) {
      if (oldId === null) {
        if (!this.courseEdit.instructors.includes(newId)) {
          this.courseEdit.instructors.pop()
          this.courseEdit.instructors.push(newId)
        } else {
          this.courseEdit.instructors.pop()
        }
      } else {
        const index = this.courseEdit.instructors.indexOf(oldId)
        if (index !== -1) {
          this.courseEdit.instructors.splice(index, 1, newId)
        }
      }
    },

    closeDialog() {
      this.dialog = false
      this.$nextTick(() => {
        this.$router.push("/kurse/" + this.courseEdit._id)
      })
    },
  },
  watch: {
    time(val) {
      if (val && this.courseEdit) {
        this.courseEdit.hours = parseInt(val.split(":")[0])
        this.courseEdit.minutes = parseInt(val.split(":")[1])
      }
    },
  },
  computed: {
    validRecipientsCount() {
      return this.smsRecipients.filter((r) => r.isValid).length
    },
    smsResultTone() {
      if (this.smsHasError || (this.smsFailed.length > 0 && this.smsSentCount === 0)) return "error"
      if (this.smsFailed.length > 0) return "warning"
      return "success"
    },
    smsResultTitle() {
      if (this.smsHasError) return "Versand nicht bestätigt"
      if (this.smsResultTone === "error") return "SMS-Versand fehlgeschlagen"
      if (this.smsResultTone === "warning") return "SMS teilweise versendet"
      return "SMS-Versand abgeschlossen"
    },
    smsResultIcon() {
      if (this.smsResultTone === "error") return "mdi-alert-circle-outline"
      if (this.smsResultTone === "warning") return "mdi-alert-outline"
      return "mdi-check-circle-outline"
    },
    exampleMessage() {
      if (this.smsRecipients.length > 0 && this.smsRecipients[0].message) {
        return this.smsRecipients[0].message
      }
      return "Keine Vorschau verfügbar."
    },
    courseActivationText() {
      return this.courseEdit.cancelled ? "Kurs reaktivieren" : "Kurs absagen"
    },
    headerImageSrc() {
      return this.courseEdit?.course?.bild ? `/img/kurse/${this.courseEdit.course.bild}` : ""
    },
    invalidRecipientsCount() {
      return this.smsRecipients.filter((r) => !r.isValid).length
    },
  },
}
</script>

<style lang="scss" scoped>
::v-deep .iconAvatar {
  width: 50px !important;
  height: 50px !important;
}
.course-edit-wrapper {
  background-color: #f7f8fa;
  height: 100vh;
  width: 100vw;
  overflow-y: auto;
}

.app-bar-border {
  border-bottom: 1px solid #e0e0e0 !important;
}

.form-container {
  padding-bottom: 24px;
  max-width: 700px;
}

.header-image-container {
  margin: -12px -12px 24px -12px;
  border-radius: 0 0 24px 24px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.form-card {
  border-radius: 16px;
  margin-bottom: 24px;
  border: 1px solid #e0e0e0;
}

::v-deep .v-chip .v-avatar .lottie-player {
  width: 32px !important;
  height: 32px !important;
}

.course-dialog.v-card {
  border-radius: 1.25rem;
  color: #24342b;
  overflow: hidden;
  font-size: 1rem;
}
.course-dialog-header {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.75rem 1.75rem 1.25rem;
  > div:last-child { min-width: 0; }
  h2 {
    margin: 0.25rem 0 0;
    font-size: 1.375rem;
    font-weight: 700;
    line-height: 1.3;
    overflow-wrap: anywhere;
  }
}
.course-dialog-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 3rem;
  height: 3rem;
  border-radius: 1rem;
  background: #edf7f0;
  &--error { background: #fceeed; }
  &--warning { background: #fff5e5; }
}
.course-dialog-eyebrow {
  color: #607066;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  line-height: 1.5;
  text-transform: uppercase;
}
.course-dialog .course-dialog-body {
  padding: 0 1.75rem 1.5rem;
  color: #536159;
  font-size: 1rem;
  line-height: 1.6;
  p { margin: 0 0 1.25rem; }
  > p:last-child { margin-bottom: 0; }
  h3 { color: #24342b; font-size: 1rem; font-weight: 700; line-height: 1.5; }
}
.course-dialog-note {
  display: flex;
  align-items: flex-start;
  gap: 0.625rem;
  padding: 0.875rem 1rem;
  border-radius: 0.75rem;
  background: #f2f6f3;
  font-size: 0.875rem;
  line-height: 1.5;
  .v-icon { margin-top: 0.125rem; flex: 0 0 auto; }
  &--warning { margin-top: 1rem; background: #fff5e5; color: #795318; }
}
.course-dialog .course-dialog-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1.25rem 1.75rem;
  border-top: 1px solid #e7ece8;
  background: #f8faf8;
  .v-btn {
    flex: 1 1 auto;
    min-height: 2.875rem;
    height: auto;
    max-width: 100%;
    margin: 0;
    padding: 0.75rem 1rem;
    border-radius: 0.75rem;
    font-size: 0.875rem;
    font-weight: 600;
    letter-spacing: normal;
    text-transform: none;
  }
  .course-dialog-secondary { color: #536159; border-color: #d4ddd6; background: white; }
  ::v-deep .v-btn__content { white-space: normal; line-height: 1.4; }
}
.sms-message {
  margin-bottom: 1.5rem;
  padding: 1.125rem;
  border: 1px solid #dce9df;
  border-radius: 1rem;
  background: #f3f8f4;
  .course-dialog-eyebrow { margin-bottom: 0.625rem; }
  p { margin: 0 !important; white-space: pre-line; overflow-wrap: anywhere; color: #2f4938; }
}
.sms-section-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  > span { font-size: 0.875rem; }
}
.sms-count {
  display: inline-block;
  margin-left: 0.375rem;
  padding: 0.125rem 0.5rem;
  border-radius: 0.5rem;
  background: #edf2ee;
  font-size: 0.75rem;
}
.course-dialog .sms-recipients {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 15rem;
  overflow-y: auto;
  border: 1px solid #e2e8e3;
  border-radius: 0.875rem;
}
.sms-recipient {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  + .sms-recipient { border-top: 1px solid #edf0ed; }
}
.sms-recipient-details { flex: 1 1 10rem; min-width: 0; overflow-wrap: anywhere; }
.sms-recipient-name { color: #24342b; font-size: 1rem; font-weight: 600; }
.sms-recipient-phone { display: flex; align-items: center; flex-wrap: wrap; gap: 0.25rem; font-size: 0.875rem; }
.sms-phone-info { display: inline-flex; align-items: center; justify-content: center; min-width: 1.5rem; min-height: 1.5rem; border-radius: 0.25rem; }
.sms-phone-info:focus-visible { outline: 2px solid #427452; outline-offset: 2px; }
.sms-phone-tooltip { font-size: 0.875rem; }
.sms-recipient-status {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  color: #356044;
  font-size: 0.75rem;
  font-weight: 600;
  &--invalid { color: #b33333; }
}
.sms-sending {
  padding: 1.5rem 0;
  text-align: center;
  h3 { margin-top: 1.25rem; margin-bottom: 0.5rem; }
  p { margin-bottom: 0 !important; }
}
.sms-result-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(7rem, 1fr));
  gap: 0.75rem;
  margin-bottom: 1.25rem;
  > div { display: flex; flex-direction: column; padding: 1rem; border-radius: 0.875rem; background: #f2f6f3; }
  strong { color: #2e6640; font-size: 1.75rem; line-height: 1.25; }
  span { margin-top: 0.25rem; font-size: 0.875rem; }
  .sms-stat-failed { background: #fceeed; strong { color: #b33333; } }
}
.sms-failures {
  margin-bottom: 1.25rem;
  padding: 1rem;
  border: 1px solid #f0d6d1;
  border-radius: 0.875rem;
  background: #fff8f7;
  ul { list-style: none; padding: 0; margin: 0.75rem 0 0; max-height: 12rem; overflow-y: auto; }
  li + li { margin-top: 0.75rem; }
  strong, span { display: block; overflow-wrap: anywhere; }
  strong { font-size: 0.875rem; color: #883d34; }
  span { font-size: 0.875rem; }
}
@media (max-width: 480px) {
  .course-dialog-header { padding: 1.25rem 1.25rem 1rem; gap: 0.75rem; }
  .course-dialog .course-dialog-body { padding: 0 1.25rem 1.25rem; }
  .course-dialog .course-dialog-actions {
    padding: 1rem 1.25rem;
    flex-direction: column;
    .v-btn { width: 100%; }
  }
}
</style>
